import {
  AuthObserver,
  circadiaEngineProvider,
  localStorageProvider,
} from "../../../../AppController";
import { parseJwt } from "../../../../util/jwt";

const env = import.meta.env;

export const AuthProviderFactory: IAuthProviderFactory = ({
  Auth,
  CircadiaEngine,
}) => {
  const isCircadiaEngine = CircadiaEngine.isWindowCircadia();

  const ssoLogin = async (authParams: {
    token: string;
    expires_at: number;
  }) => {
    // Use the current user pool from environment variables
    const region = env.VITE_AWS_REGION || "us-east-1";
    const userPoolId = env.VITE_COGNITO_USER_POOL_ID;
    const providerId = `cognito-idp.${region}.amazonaws.com/${userPoolId}`;

    const res = await Auth.federatedSignIn(providerId, authParams);
    return res;
  };

  const signIn = async (email: string, password: string) => {
    if (
      isCircadiaEngine &&
      circadiaEngineProvider.intfv >= 5 &&
      circadiaEngineProvider.isIOS
    ) {
      return new Promise<User>((resolve, reject) => {
        const observerEvent = ({
          type,
          payload,
        }: {
          type: string;
          payload: {
            success: boolean;
            error?: string;
            props?: unknown;
          };
        }) => {
          AuthObserver.unsubscribe(observerEvent as (value: unknown) => void);
          switch (type) {
            case "login":
              if (payload.success) {
                resolve({
                  email: email as string,
                  userID: "",
                  role: "",
                  firstName: "",
                  lastName: "",
                });
              } else {
                reject(payload.error);
              }
              break;
            case "mfaRequired":
              resolve({
                email: email as string,
                userID: "",
                role: "",
                firstName: "",
                lastName: "",
                challengeName: "SMS_MFA",
                challengeParam: {
                  CODE_DELIVERY_DESTINATION: payload.props as string,
                  CODE_DELIVERY_DELIVERY_MEDIUM: "",
                },
              });
              break;
            case "newPasswordRequired":
              resolve({
                email: email as string,
                userID: "",
                role: "",
                firstName: "",
                lastName: "",
                challengeName: "NEW_PASSWORD_REQUIRED",
              });
              break;
            case "passwordlessOTPRequired":
              resolve({
                email: email as string,
                userID: "",
                role: "",
                firstName: "",
                lastName: "",
                challengeName: "CUSTOM_CHALLENGE",
              });
              break;
            default:
              reject(null);
              break;
          }
        };

        AuthObserver.subscribe(observerEvent as (value: unknown) => void);
        if (password) {
          CircadiaEngine.cognitoUserLogin(email, password);
        } else {
          CircadiaEngine.cognitoUserEmailOTPLogin(email);
        }
      });
    } else {
      if (password === "") {
        // Web-only CUSTOM_AUTH OTP path: temporarily override the flow type so
        // Amplify's default "fast" config (USER_SRP_AUTH) doesn't swallow the
        // InitiateAuth(CUSTOM_AUTH) call and the Cognito Lambda never fires.
        Auth.configure({ authenticationFlowType: "CUSTOM_AUTH" });
        try {
          const user = await Auth.signIn(email);
          // The Cognito Lambda is a 2-stage state machine. Stage 1 returns
          // CUSTOM_CHALLENGE with challenge="INIT" — it is a gate that asks
          // which channel to use before it actually generates and dispatches
          // the OTP. We must ACK it here (mirrors what the native plugin does).
          if (
            user?.challengeName === "CUSTOM_CHALLENGE" &&
            (user?.challengeParam as any)?.challenge === "INIT"
          ) {
            return await Auth.sendCustomChallengeAnswer(user, "__INIT__", {
              loginMethod: "email",
            });
          }
          return user;
        } finally {
          // Restore the "fast" config default so subsequent password sign-ins
          // and token refreshes continue to use USER_SRP_AUTH.
          Auth.configure({ authenticationFlowType: "USER_SRP_AUTH" });
        }
      }
      return Auth.signIn(email, password);
    }
  };

  const confirmSignIn = async (
    user: User,
    code: string,
    challengeType:
      | "SMS_MFA"
      | "SOFTWARE_TOKEN_MFA"
      | "CUSTOM_CHALLENGE"
      | null
      | undefined
  ) => {
    if (
      isCircadiaEngine &&
      circadiaEngineProvider.intfv >= 5 &&
      circadiaEngineProvider.isIOS
    ) {
      const mfaResponse = await new Promise((resolve, reject) => {
        const observerEvent = ({
          type,
          payload,
        }: {
          type: string;
          payload: { success: boolean; error: string | null };
        }) => {
          AuthObserver.unsubscribe(observerEvent as (value: unknown) => void);
          switch (type) {
            case "login":
              if (payload.success) {
                resolve({
                  email: user.email,
                  userID: "",
                  role: "",
                  firstName: "",
                  lastName: "",
                });
              } else {
                reject({ message: payload.error || "" });
              }
              break;
            case "pinRequired":
              resolve({
                email: user.email,
                userID: "",
                role: "",
                firstName: "",
                lastName: "",
                challengeName: "PIN",
              });
              break;
            case "passwordlessOTPRequired":
              reject({ message: "Invalid code" });
              break;
            default:
              reject(null);
              break;
          }
        };
        AuthObserver.subscribe(observerEvent as (value: unknown) => void);
        if (challengeType === "CUSTOM_CHALLENGE") {
          CircadiaEngine.cognitoProvideEmailOTP(code);
        } else {
          CircadiaEngine.cognitoProvideMfa(code);
        }
      });
      return mfaResponse as User | null;
    } else {
      if (challengeType === "CUSTOM_CHALLENGE") {
        const res = await Auth.sendCustomChallengeAnswer(user, code);
        if (res?.challengeName === "CUSTOM_CHALLENGE") {
          throw new Error("Invalid code", {
            cause: res,
          });
        }
        return res;
      }
      return Auth.confirmSignIn(user, code, challengeType);
    }
  };

  const signOut = async () => {
    if (
      isCircadiaEngine &&
      circadiaEngineProvider.intfv >= 5 &&
      circadiaEngineProvider.isIOS
    ) {
      localStorageProvider.remove("userInfoUpdated");
      await Auth.signOut();
      return circadiaEngineProvider.cognitoUserLogout();
    } else {
      return Auth.signOut();
    }
  };

  const completeNewPassword = async (
    user: User,
    password: string,
    additionalProps: {
      email: string;
    }
  ) => {
    if (
      isCircadiaEngine &&
      circadiaEngineProvider.intfv >= 5 &&
      circadiaEngineProvider.isIOS
    ) {
      return new Promise<User>((resolve, reject) => {
        const observerEvent = ({
          type,
          payload,
        }: {
          type: string;
          payload: {
            success: boolean;
            error?: string;
            props?: unknown;
          };
        }) => {
          AuthObserver.unsubscribe(observerEvent as (value: unknown) => void);
          switch (type) {
            case "login":
              if (payload.success) {
                resolve({
                  email: user.email,
                  userID: "",
                  role: "",
                  firstName: "",
                  lastName: "",
                });
              } else {
                reject(payload.error);
              }
              break;
            case "mfaRequired":
              resolve({
                email: user.email,
                userID: "",
                role: "",
                firstName: "",
                lastName: "",
                challengeName: "SMS_MFA",
                challengeParam: {
                  CODE_DELIVERY_DESTINATION: payload.props as string,
                  CODE_DELIVERY_DELIVERY_MEDIUM: "",
                },
              });
              break;
            case "newPasswordRequired":
              resolve({
                email: user.email,
                userID: "",
                role: "",
                firstName: "",
                lastName: "",
                challengeName: "NEW_PASSWORD_REQUIRED",
              });
              break;
            default:
              reject(null);
              break;
          }
        };

        AuthObserver.subscribe(observerEvent as (value: unknown) => void);
        CircadiaEngine.cognitoProvideNewPassword(password, additionalProps);
      });
    } else {
      return Auth.completeNewPassword(user, password, additionalProps);
    }
  };

  const forgotPassword = (email: string) => {
    return Auth.forgotPassword(email);
  };

  const resetPassword = (email: string, code: string, password: string) => {
    return Auth.forgotPasswordSubmit(email, code, password);
  };

  return {
    signIn,

    ssoLogin,

    currentSession: () => Auth.currentSession(),

    currentUserInfo: async () => {
      if (localStorageProvider.get("zpax") === "true") {
        try {
          // For SSO and ZPAX, get user info from current session
          const session = await Auth.currentSession();

          const idToken = session.getIdToken();

          const payload = idToken.payload;

          return {
            attributes: {
              "custom:first_name": Array.isArray(payload["custom:first_name"])
                ? payload["custom:first_name"][0]
                : payload["custom:first_name"],
              "custom:last_name": Array.isArray(payload["custom:last_name"])
                ? payload["custom:last_name"][0]
                : payload["custom:last_name"],
              sub: Array.isArray(payload.sub) ? payload.sub[0] : payload.sub,
              email: Array.isArray(payload.email)
                ? payload.email[0]
                : payload.email ||
                  (Array.isArray(payload["custom:pcc_email"])
                    ? payload["custom:pcc_email"][0]
                    : payload["custom:pcc_email"]),
              "custom:home_health_id": Array.isArray(
                payload["custom:home_health_id"]
              )
                ? payload["custom:home_health_id"][0]
                : payload["custom:home_health_id"],
            },
          };
        } catch (error) {
          console.error("Error getting current user info for SSO/ZPAX:", error);
          // Fallback to stored token for ZPAX
          if (localStorageProvider.get("zpax") === "true") {
            const storedToken = localStorageProvider.get("idToken");
            if (storedToken) {
              const user = parseJwt(storedToken);
              return {
                attributes: {
                  "custom:first_name": user["custom:first_name"] || "",
                  "custom:last_name": user["custom:last_name"] || "",
                  sub: user.sub || "",
                  email: user.email || "",
                  "custom:home_health_id": user["custom:home_health_id"] || "",
                },
              };
            }
          }
          throw error;
        }
      } else if (localStorageProvider.get("microsoftSso") === "true") {
        // For Microsoft SSO, get user info from stored ID token
        const storedToken = localStorageProvider.get("idToken");
        if (storedToken) {
          const user = parseJwt(storedToken);
          return {
            attributes: {
              "custom:first_name":
                user.given_name || user["custom:first_name"] || "",
              "custom:last_name":
                user.family_name || user["custom:last_name"] || "",
              sub: user.sub || "",
              email: user.email || user.preferred_username || "",
              "custom:home_health_id": user["custom:home_health_id"] || "",
              "custom:pcc_org_uuid": user["custom:pcc_org_uuid"] || "",
              "custom:pcc_email": user["custom:pcc_email"] || "",
              phone_number: user["phone_number"] || "",
              "custom:m_credentials": user["custom:m_credentials"] || "",
            },
          };
        }
        // Fallback to current authenticated user
        return Auth.currentUserInfo();
      } else if (localStorageProvider.get("sso") === "true") {
        const user = await Auth.currentAuthenticatedUser();
        const token = (user as any).token;

        if (token) {
          const parsedToken = parseJwt(token);
          return {
            attributes: {
              "custom:first_name": parsedToken["custom:first_name"],
              "custom:last_name": parsedToken["custom:last_name"],
              sub: parsedToken.sub,
              email: parsedToken["custom:pcc_email"],
              "custom:home_health_id": parsedToken["custom:home_health_id"],
              "custom:pcc_org_uuid": parsedToken["custom:pcc_org_uuid"],
              "custom:pcc_email": parsedToken["custom:pcc_email"],
              phone_number: parsedToken["phone_number"],
              "custom:m_credentials": parsedToken["custom:m_credentials"],
            },
          };
        }
      } else {
        return Auth.currentUserInfo();
      }
    },

    currentAuthenticatedUser: () => Auth.currentAuthenticatedUser(),

    completeNewPassword,

    confirmSignIn,

    changePassword: async (
      user: User | string,
      oldPassword: string,
      newPassword: string
    ) => {
      if (
        isCircadiaEngine &&
        circadiaEngineProvider.intfv >= 5 &&
        circadiaEngineProvider.isIOS
      ) {
        const res = await CircadiaEngine.cognitoChangePassword(
          user as string,
          oldPassword,
          newPassword
        );
        if (res.code === 0) {
          return "SUCCESS";
        }

        throw new Error(res.mdg);
      }
      return Auth.changePassword(user as User, oldPassword, newPassword);
    },

    signOut,

    configure: (config: Record<string, string>) => Auth.configure(config),

    forgotPassword,

    resetPassword,
  };
};
